import { useMemo } from 'react';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
// eslint-disable-next-line no-console
const processError = (error) => console.error(error);
const generateKey = (scope, key) => (scope ? `${scope}_${key}` : key);
const getItemSafe = (storage, key, scope) => {
    try {
        const scopedKey = generateKey(scope, key);
        return storage.getItem(scopedKey);
    }
    catch (e) {
        processError(e);
        return null;
    }
};
const removeItemSafe = (storage, key, scope) => {
    try {
        const scopedKey = generateKey(scope, key);
        storage.removeItem(scopedKey);
    }
    catch (e) {
        processError(e);
    }
};
const setItemSafe = (storage, key, scope, value) => {
    try {
        const scopedKey = generateKey(scope, key);
        storage.setItem(scopedKey, value);
    }
    catch (e) {
        processError(e);
    }
};
const useStorage = ({ scope, storage, }) => {
    const removeItem = useCallbackRef((key, customScope) => removeItemSafe(storage, key, customScope ?? scope));
    const loadString = useCallbackRef((key) => getItemSafe(storage, key, scope));
    const loadNumber = useCallbackRef((key) => {
        const value = getItemSafe(storage, key, scope);
        return value !== null ? +value : null;
    });
    const loadBoolean = useCallbackRef((key) => !!getItemSafe(storage, key, scope));
    const loadJSON = useCallbackRef((key, onError, customScope) => {
        try {
            const value = getItemSafe(storage, key, customScope ?? scope);
            return value && JSON.parse(value);
        }
        catch (error) {
            onError?.(error);
            return null;
        }
    });
    const saveString = useCallbackRef((key, data) => setItemSafe(storage, key, scope, data));
    const saveNumber = useCallbackRef((key, data) => setItemSafe(storage, key, scope, data.toString()));
    const saveBoolean = useCallbackRef((key, data) => {
        if (loadBoolean(key)) {
            removeItem(key);
            return;
        }
        setItemSafe(storage, key, scope, data.toString());
    });
    const saveJSON = useCallbackRef((key, data, customScope) => storage.setItem(generateKey(customScope ?? scope, key), JSON.stringify(data)));
    return useMemo(() => ({
        removeItem,
        loadString,
        loadNumber,
        loadBoolean,
        loadJSON,
        saveString,
        saveNumber,
        saveBoolean,
        saveJSON,
    }), [removeItem, loadString, loadNumber, loadBoolean, loadJSON, saveString, saveNumber, saveBoolean, saveJSON]);
};
export default useStorage;
